import { Button } from "~/components/ui/button";
import { Picture } from "~/components/ui/picture";
import { ProductWordmark, hasProductWordmark } from "~/components/products/ProductWordmark";

export interface ProductUpsellCardProps {
  /** Accessible name of the wordmark; shown as text only when the product has no wordmark. */
  name: string;
  description: string;
  image: string;
  /** Shown below md; falls back to `image`. */
  mobileImage?: string;
  imageAlt: string;
  /** Real product code, matching the embedded cart catalog directly, e.g. "bouncieobd". */
  productCode: string;
  href: string;
  ctaLabel: string;
  /** Descriptive accessible name for the CTA — visible label stays "Learn More" per Figma. */
  ariaLabel: string;
}

// Images are pre-composed to the panel's 343x250 (162x134 below md) frame, so object-contain
// places them as designed. Below md the card is Figma's compact, center-aligned variant — it
// grows with the viewport between its 162px Figma width and 220px (keeping the 162x134 ratio via
// aspect-[] instead of a fixed height) so it isn't stranded tiny on larger phones, then caps out
// and centers in the row rather than ballooning toward tablet width. md and up is the fixed-size
// tablet/desktop variant (identical at both sizes).
export function ProductUpsellCard({ name, description, image, mobileImage, imageAlt, productCode, href, ctaLabel, ariaLabel }: ProductUpsellCardProps) {
  return (
    <div className="flex max-w-[220px] min-w-[162px] flex-1 flex-col md:w-full md:max-w-[343px] md:flex-none">
      <div className="bg-figma-background-surface-bg-secondary border-border aspect-[162/134] w-full shrink-0 overflow-hidden rounded-t-2xl border border-b-0 md:aspect-auto md:h-[250px]">
        <Picture
          src={image}
          mobileSrc={mobileImage}
          desktopMedia="(min-width: 768px)"
          alt={imageAlt}
          className="h-full w-full object-contain"
        />
      </div>
      <div className="border-border flex w-full flex-1 flex-col gap-3 rounded-b-2xl border border-t-0 bg-white p-4 md:gap-4 md:p-6">
        <div className="flex w-full flex-col items-center gap-2 text-center md:items-start md:text-left">
          {hasProductWordmark(productCode) ? (
            <ProductWordmark productCode={productCode} name={name} className="h-4 text-black md:h-[22px]" />
          ) : (
            <p className="font-display text-text-heading text-lg leading-[1.15] font-bold md:text-xl">{name}</p>
          )}
          <p className="text-text-body-alt text-xs leading-[18px] md:text-base md:leading-[1.5]">{description}</p>
        </div>
        {/* mt-auto keeps CTAs aligned when a sibling card's description wraps */}
        <Button
          variant="tertiary"
          size="sm"
          className="bg-bg-brand-subtle mt-auto h-11 w-full text-base leading-5 tracking-[0.16px]"
          asChild
        >
          <a data-sid={`pdp-upsell-cta:${href}`} href={href} aria-label={ariaLabel}>{ctaLabel}</a>
        </Button>
      </div>
    </div>
  );
}
